import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaDownload } from 'react-icons/fa6';
import PaginationFooter from 'shared/components/Logs/PaginationFooter';
import ProtectedButton from 'shared/components/ProtectedButton';
import IssueManyModal from './components/IssueManyModal';
import NfseReportFilters from './components/NfseReportFilters';
import NfseReportTable from './components/NfseReportTable';
import { formatCurrency } from './helpers/csvValidation';
import { exportNfseXlsx, fetchAllNfseRecords } from './helpers/exportNfseXlsx';
import { buildListParams, useNfseReportContext } from './NfseReportContext';
import {
  NFSE_REPORT_STATUS_COLORS,
  NFSE_REPORT_STATUS_LABELS,
  NfseReportStatus,
} from './types/nfse.types';

const NfseReport = () => {
  const {
    data,
    totalsByStatus,
    loading,
    error,
    page,
    setPage,
    lastPage,
    filters,
    fetchData,
  } = useNfseReportContext();

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBgColor = useColorModeValue('gray.50', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    fetched: 0,
    total: 0,
  });

  const handleExport = async () => {
    setExporting(true);
    setExportProgress({ fetched: 0, total: 0 });
    try {
      const params = buildListParams(filters);
      const allRows = await fetchAllNfseRecords(params, (fetched, total) => {
        setExportProgress({ fetched, total });
      });
      exportNfseXlsx(allRows);
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível exportar o relatório.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colorScheme =
      NFSE_REPORT_STATUS_COLORS[status as NfseReportStatus] || 'gray';
    return `${colorScheme}.500`;
  };

  const getStatusLabel = (status: string): string => {
    return NFSE_REPORT_STATUS_LABELS[status as NfseReportStatus] || status;
  };

  return (
    <Stack gap="1.5rem">
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Heading size="lg" fontWeight="500" color="gray.600">
          Relatório de DPS
        </Heading>

        <Button
          size="sm"
          colorScheme="yooga"
          variant="outline"
          leftIcon={<FaDownload />}
          isLoading={exporting}
          loadingText={`Exportando… ${exportProgress.fetched}/${exportProgress.total}`}
          isDisabled={data.length === 0 || exporting}
          onClick={handleExport}
        >
          Exportar Excel
        </Button>
      </HStack>

      {totalsByStatus.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
          {totalsByStatus.map((item) => (
            <Card key={item.status} bg={cardBgColor}>
              <CardBody py={3} px={4}>
                <Stat size="sm">
                  <StatLabel
                    color={getStatusColor(item.status)}
                    fontWeight="bold"
                  >
                    {getStatusLabel(item.status)}
                  </StatLabel>
                  <StatNumber fontSize="lg">{item.count}</StatNumber>
                  <Text fontSize="xs" color="gray.500">
                    {formatCurrency(item.totalValue)}
                  </Text>
                </Stat>
              </CardBody>
            </Card>
          ))}
          <Card bg={cardBgColor}>
            <CardBody py={3} px={4}>
              <Stat size="sm">
                <StatLabel fontWeight="bold" color="gray.600">
                  Total
                </StatLabel>
                <StatNumber fontSize="lg">
                  {totalsByStatus.reduce((sum, item) => sum + item.count, 0)}
                </StatNumber>
                <Text fontSize="xs" color="gray.500">
                  {formatCurrency(
                    totalsByStatus.reduce(
                      (sum, item) => sum + item.totalValue,
                      0,
                    ),
                  )}
                </Text>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBgColor}>
            <CardBody
              py={3}
              px={4}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ProtectedButton
                permission="nfse-create"
                colorScheme="yooga"
                onClick={onOpen}
              >
                Emissão Mensal
              </ProtectedButton>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      <IssueManyModal isOpen={isOpen} onClose={onClose} />

      <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
        <Stack spacing={4}>
          <NfseReportFilters />

          {loading && (
            <HStack justify="center" py="4rem">
              <Spinner size="lg" color="yooga.500" />
              <Text>Carregando dados...</Text>
            </HStack>
          )}

          {error && !loading && (
            <Stack align="center" py="4rem" gap="1rem">
              <Text color="red.500">{error}</Text>
              <Button onClick={fetchData} colorScheme="yooga" variant="outline">
                Tentar novamente
              </Button>
            </Stack>
          )}

          {!loading && !error && data.length === 0 && (
            <Flex justify="center" align="center" py="4rem">
              <Text color="gray.500">Nenhum registro encontrado</Text>
            </Flex>
          )}

          {!loading && !error && data.length > 0 && (
            <>
              <NfseReportTable data={data} />

              <PaginationFooter
                visibleLength={data.length}
                page={page}
                setPage={setPage}
                pageCount={lastPage}
              />
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default NfseReport;
